/**
 * Sidebar brand block and sign-out row.
 *
 * The redesign uses a "GL" monogram plus the product name rather than the tenant logo — this is
 * the admin panel, not a branded dashboard, so it stays the same whichever tenant you are
 * looking at.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { JSX } from 'react';

const brandStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const monogramStyle = css`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--gl-blue);
  color: var(--gl-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.02em;
`;

const brandTextStyle = css`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
`;

const brandNameStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-bold);
`;

const brandSubtitleStyle = css`
  font-size: 11px;
  color: var(--color-text-muted);
`;

export const SidebarBrand = (): JSX.Element => (
  <div className={brandStyle}>
    <span className={monogramStyle}>GL</span>
    <div className={brandTextStyle}>
      <span className={brandNameStyle}>BFF Apps</span>
      <span className={brandSubtitleStyle}>Administração</span>
    </div>
  </div>
);

const signOutStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 42px;
  padding: 0 12px;
  border: none;
  background: transparent;
  border-radius: var(--radius-m);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  width: 100%;
  transition: background var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-hover);
  }
`;

export interface SidebarSignOutProps {
  onSignOut: () => void;
}

export const SidebarSignOut = ({ onSignOut }: SidebarSignOutProps): JSX.Element => (
  <button id='sidebar-sign-out' type='button' className={signOutStyle} onClick={onSignOut}>
    <GLIcon variant='Arrows.SignOut' size={20} fill='var(--color-text-muted)' />
    <span>Sair</span>
  </button>
);
